import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  CategoryKind,
  PlannedMovementKind,
  PlannedMovementStatus,
  RecurringSourceKind,
  isWithinCycle,
  type BudgetView,
  type CardPayment,
  type CcCharge,
  type CcChargeCaptureMode,
  type CreditCard,
  type HistoricalBalanceTreatment,
  type InstallmentsResponse,
} from '@financial/shared';
import {
  useCard,
  useCharges,
  useCloseCycle,
  useCreateCharge,
  useCreatePayment,
  useDeleteCharge,
  useDeletePayment,
  useInstallments,
  usePayments,
  useProjection,
  useUpdateCharge,
  useUpsertCard,
} from '../features/creditCard/useCreditCard';
import { useAccounts } from '../features/accounts/useAccounts';
import { useCategories } from '../features/categories/useCategories';
import { currentCurrency, formatDate, formatMoney, toDateInputValue } from '../lib/format';
import { getApiErrorMessage } from '../lib/errors';
import { Field } from '../components/ui/Field';
import { DatePicker } from '../components/ui/DatePicker';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { MonthPicker } from '../components/ui/DatePicker';
import { GuidedPageHeader } from '../components/onboarding/GuidedPageHeader';
import { Button, Card, HelperText, Skeleton } from '../components/ui/Primitives';
import { SkeletonState } from '../components/ui/SkeletonState';
import { useBudget } from '../features/budgets/useBudgets';
import { getPurchasePolicy, type PurchasePolicy } from '../lib/purchaseCapacity';
import { usePlanned } from '../features/planned/usePlanned';
import { summarizeHistoricalMsi } from '../lib/historicalMsi';
import { currentYearMonth } from '../lib/businessDate';
import { Tooltip } from '../components/ui/Tooltip';

function isValidDateInput(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

function readLocalFlag(key: string): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(key) === 'none';
  } catch {
    return false;
  }
}

function writeLocalFlag(key: string): void {
  try {
    window.localStorage.setItem(key, 'none');
  } catch {
    // El aviso se oculta durante esta visita aunque el navegador bloquee almacenamiento local.
  }
}

function createCardAttemptKey(): string {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  const randomPart =
    typeof cryptoApi?.getRandomValues === 'function'
      ? Array.from(cryptoApi.getRandomValues(new Uint32Array(4)), (value) =>
          value.toString(36),
        ).join('-')
      : `${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
  return `cc-${Date.now().toString(36)}-${randomPart}`;
}

function cutoffDate(year: number, month: number, cutoffDay: number): Date {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(cutoffDay, lastDay), 23, 59, 59, 999);
}

function isInCurrentCycle(charge: CcCharge, cutoffDay: number): boolean {
  const now = new Date();
  const thisCutoff = cutoffDate(now.getFullYear(), now.getMonth(), cutoffDay);
  const end =
    now <= thisCutoff ? thisCutoff : cutoffDate(now.getFullYear(), now.getMonth() + 1, cutoffDay);
  const previousCutoff = cutoffDate(end.getFullYear(), end.getMonth() - 1, cutoffDay);
  const start = new Date(previousCutoff);
  start.setDate(start.getDate() + 1);
  start.setHours(0, 0, 0, 0);
  const purchase = new Date(charge.purchaseDate);
  return purchase >= start && purchase <= end;
}

export function CreditCardPage() {
  const cardQ = useCard();
  const budgetQ = useBudget(currentYearMonth());
  const [searchParams, setSearchParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(() => searchParams.get('accion') === 'nuevo-cargo');
  const [configOpen, setConfigOpen] = useState(() => searchParams.get('accion') === 'configurar');
  const [addMode, setAddMode] = useState<CcChargeCaptureMode>('new');

  useEffect(() => {
    const action = searchParams.get('accion');
    if (action === 'nuevo-cargo') {
      setAddMode(searchParams.get('tipo') === 'anterior' ? 'historical' : 'new');
      setAddOpen(true);
    }
    if (action === 'configurar') setConfigOpen(true);
  }, [searchParams]);

  const openAdd = (mode: CcChargeCaptureMode = 'new') => {
    setAddMode(mode);
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    if (searchParams.get('accion') === 'nuevo-cargo') {
      const next = new URLSearchParams(searchParams);
      next.delete('accion');
      next.delete('tipo');
      setSearchParams(next, { replace: true });
    }
  };

  const closeConfig = () => {
    setConfigOpen(false);
    if (searchParams.get('accion') === 'configurar') {
      const next = new URLSearchParams(searchParams);
      next.delete('accion');
      setSearchParams(next, { replace: true });
    }
  };

  if (cardQ.isLoading) {
    return (
      <div className="reference-page w-full">
        <GuidedPageHeader
          section="creditCard"
          title="Tarjeta de crédito"
          description="Consulta tu deuda, compras y próximos pagos."
        />
        <CreditCardSkeleton />
      </div>
    );
  }

  if (cardQ.isError) {
    return (
      <div className="reference-page w-full">
        <GuidedPageHeader
          section="creditCard"
          title="Tarjeta de crédito"
          description="Consulta tu deuda, compras y próximos pagos."
        />
        <Card className="mx-auto max-w-md text-center">
          <div role="alert">
            <h2 className="text-base font-semibold text-slate-900">No pudimos cargar tu tarjeta</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Hubo un problema al consultar tus datos. Vuelve a intentarlo antes de hacer cambios.
            </p>
          </div>
          <Button className="mt-4" disabled={cardQ.isFetching} onClick={() => void cardQ.refetch()}>
            {cardQ.isFetching ? 'Intentando de nuevo…' : 'Volver a intentar'}
          </Button>
        </Card>
      </div>
    );
  }

  if (cardQ.data === null) {
    return (
      <div className="reference-page w-full">
        <GuidedPageHeader
          section="creditCard"
          title="Tarjeta de crédito"
          description="Configura las fechas de corte y pago para calcular cuánto podrías tener que pagar."
        />
        <div className="ui-card mx-auto max-w-md">
          <CardForm onDone={closeConfig} />
        </div>
      </div>
    );
  }

  if (cardQ.data === undefined) {
    return (
      <div className="reference-page w-full">
        <GuidedPageHeader
          section="creditCard"
          title="Tarjeta de crédito"
          description="Consulta tu deuda, compras y próximos pagos."
        />
        <CreditCardSkeleton />
      </div>
    );
  }

  const card = cardQ.data;

  return (
    <CreditCardDashboard
      card={card}
      budget={budgetQ.data}
      addOpen={addOpen}
      configOpen={configOpen}
      addMode={addMode}
      onOpenAdd={openAdd}
      onCloseAdd={closeAdd}
      onOpenConfig={() => setConfigOpen(true)}
      onCloseConfig={closeConfig}
    />
  );
}

export function CreditCardSkeleton() {
  return (
    <SkeletonState label="Cargando tarjeta…" className="space-y-4">
      <Skeleton className="h-28 w-full" />
      <div className="grid gap-4 lg:grid-cols-[minmax(280px,.78fr)_minmax(0,1.22fr)]">
        <Skeleton className="h-72 w-full" />
        <div className="space-y-4">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-36 w-full" />
        </div>
      </div>
    </SkeletonState>
  );
}

function CreditCardDashboard({
  card,
  budget,
  addOpen,
  configOpen,
  addMode,
  onOpenAdd,
  onCloseAdd,
  onOpenConfig,
  onCloseConfig,
}: {
  card: CreditCard;
  budget?: BudgetView;
  addOpen: boolean;
  configOpen: boolean;
  addMode: CcChargeCaptureMode;
  onOpenAdd: (mode?: CcChargeCaptureMode) => void;
  onCloseAdd: () => void;
  onOpenConfig: () => void;
  onCloseConfig: () => void;
}) {
  const installmentsQ = useInstallments();
  const chargesQ = useCharges();
  const historicalCharges = useMemo(
    () =>
      (chargesQ.data ?? []).filter(
        (charge) => charge.captureMode === 'historical' && charge.msiPaid < charge.msiMonths,
      ),
    [chargesQ.data],
  );
  const currentMonthlyMsi = useMemo(
    () =>
      (installmentsQ.data?.charges ?? []).reduce((sum, charge) => sum + charge.monthlyAmount, 0),
    [installmentsQ.data?.charges],
  );
  const policy = budget ? getPurchasePolicy(card, budget, { currentMonthlyMsi }) : undefined;

  return (
    <div className="reference-page w-full">
      <GuidedPageHeader
        section="creditCard"
        title="Tarjeta de crédito"
        description="Separa las compras del pago para no contar dos veces el mismo gasto."
        actions={
          <div className="flex flex-wrap justify-end gap-2">
            <Tooltip
              content="Cambia el límite y los días de corte o pago"
              side="bottom"
              align="end"
            >
              <Button variant="secondary" onClick={onOpenConfig}>
                Editar datos
              </Button>
            </Tooltip>
            <Tooltip
              content="Registra un cargo de la tarjeta; su pago se captura por separado"
              side="bottom"
              align="end"
            >
              <Button onClick={() => onOpenAdd('new')}>+ Compra</Button>
            </Tooltip>
          </div>
        }
      />

      <CardCoverageNotice
        cardId={card.id}
        loading={chargesQ.isLoading}
        historicalCount={historicalCharges.length}
        onAddHistorical={() => onOpenAdd('historical')}
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(280px,.78fr)_minmax(0,1.22fr)]">
        <div>
          <div className="mb-4 rounded-[16px] bg-slate-900 p-5 text-white shadow-[0_12px_28px_-18px_rgba(15,23,42,.65)]">
            <p className="text-xs text-slate-400">{card.institution ?? 'Tarjeta de crédito'}</p>
            <p className="mt-0.5 text-base font-semibold text-white">{card.name}</p>
            <p className="mt-1.5 text-xs text-slate-400">
              Corte día {card.cutoffDay} · Pago día {card.paymentDay} · Usado{' '}
              {formatMoney(card.balance, card.currency)} de{' '}
              {formatMoney(card.creditLimit, card.currency)}
            </p>
          </div>
          <ProjectionCard cardId={card.id} />
        </div>
        <div>
          <PurchaseCapacityCard policy={policy} />
          <InstallmentsSection data={installmentsQ.data} loading={installmentsQ.isLoading} />
        </div>
      </div>
      <PaymentsSection />
      <ChargesSection card={card} />

      <Modal
        open={addOpen}
        title={addMode === 'historical' ? 'Agregar compra anterior a meses' : 'Registrar compra'}
        size="wide"
        onClose={onCloseAdd}
      >
        <NewChargeForm
          key={addMode}
          card={card}
          policy={policy}
          initialMode={addMode}
          onDone={onCloseAdd}
        />
      </Modal>
      <Modal open={configOpen} title="Editar datos de la tarjeta" onClose={onCloseConfig}>
        <CardForm card={card} onDone={onCloseConfig} />
      </Modal>
    </div>
  );
}

function CardCoverageNotice({
  cardId,
  loading,
  historicalCount,
  onAddHistorical,
}: {
  cardId: string;
  loading: boolean;
  historicalCount: number;
  onAddHistorical: () => void;
}) {
  const storageKey = `card-msi-coverage-reviewed:${cardId}`;
  const [dismissed, setDismissed] = useState(() => readLocalFlag(storageKey));
  if (loading) return null;
  const hasHistorical = historicalCount > 0;
  if (!hasHistorical && dismissed) return null;

  const confirmNone = () => {
    writeLocalFlag(storageKey);
    setDismissed(true);
  };

  return (
    <section
      className={`mb-4 flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between ${
        hasHistorical ? 'border-emerald-100 bg-emerald-50/70' : 'border-amber-200 bg-amber-50/70'
      }`}
      aria-label="Datos incluidos en el cálculo estimado de la tarjeta"
    >
      <div className="min-w-0">
        <p
          className={`text-[11px] font-bold uppercase tracking-[.07em] ${hasHistorical ? 'text-emerald-800' : 'text-amber-800'}`}
        >
          Datos de tu cálculo estimado
        </p>
        <h2 className="mt-0.5 text-sm font-bold text-slate-900">
          {hasHistorical
            ? historicalCount === 1
              ? '1 compra anterior con pagos pendientes'
              : `${historicalCount} compras anteriores con pagos pendientes`
            : '¿Ya pagabas alguna compra a meses antes de usar la app?'}
        </h2>
        <p className="mt-1 max-w-3xl text-xs leading-5 text-slate-600">
          {hasHistorical
            ? 'El cálculo ya toma en cuenta esos pagos. Si falta otra compra, el pago estimado puede quedar más bajo de lo real.'
            : 'Agrégala para incluir lo que todavía falta por pagar. Mientras falte, el próximo pago que muestra la app puede quedar más bajo de lo real.'}
        </p>
      </div>
      <div className="flex shrink-0 flex-col gap-2 min-[380px]:flex-row">
        {!hasHistorical && (
          <button
            type="button"
            onClick={confirmNone}
            className="tap-target rounded-xl px-3.5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-white/70"
          >
            No tengo compras anteriores
          </button>
        )}
        <button
          type="button"
          onClick={onAddHistorical}
          className="tap-target rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:border-slate-400"
        >
          {hasHistorical ? 'Agregar otra' : 'Agregar compra anterior'}
        </button>
      </div>
    </section>
  );
}

/** Un control de flujo: no convierte el límite del banco en permiso de gasto. */
function PurchaseCapacityCard({ policy }: { policy?: PurchasePolicy }) {
  const [simulatorOpen, setSimulatorOpen] = useState(false);

  if (!policy) {
    return (
      <section className="ui-card mb-4 animate-pulse">
        <div className="h-4 w-48 rounded bg-slate-100" />
        <div className="mt-3 h-8 w-32 rounded bg-slate-100" />
      </section>
    );
  }

  return (
    <>
      <section className="mb-4 overflow-hidden rounded-[18px] border border-blue-100 bg-[linear-gradient(120deg,#eff6ff_0%,#ffffff_60%,#ecfdf5_100%)] p-4 shadow-[0_12px_28px_-20px_rgba(37,99,235,.35)] sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[.08em] text-savings">
              Control de compras
            </p>
            <h2 className="mt-1 text-lg font-bold tracking-tight text-slate-900">
              Tope recomendado para compras nuevas
            </h2>
            <p className="mt-1 max-w-xl text-sm leading-5 text-slate-600">
              Se calcula con el dinero que normalmente recibes y gastas cada mes, no con el límite
              que te presta el banco. Primero se respetan tus apartados y pagos pendientes.
            </p>
          </div>
          <Button variant="secondary" onClick={() => setSimulatorOpen(true)}>
            Simular compra
          </Button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <CapacityMetric
            label="Mensualidades que ya pagas"
            value={`${formatMoney(policy.currentMonthlyMsi)}/mes`}
            detail={`Tope sugerido: ${formatMoney(policy.monthlyMsiCeiling)}/mes (10% de tus ingresos habituales)`}
          />
          <CapacityMetric
            label="Precio total recomendado"
            value={formatMoney(policy.purchasePriceCap)}
            detail="Aplica aunque dividas la compra en mensualidades"
          />
          <CapacityMetric
            label="Espacio para otra mensualidad"
            value={`${formatMoney(policy.newMonthlyMsiCapacity)}/mes`}
            detail="Si marca $0, lo más prudente es no agregar otra compra a meses"
          />
        </div>

        <div className="mt-4 grid gap-2 border-t border-blue-100 pt-3 text-xs leading-5 text-slate-600 sm:grid-cols-3">
          <p>
            <span className="font-semibold text-slate-700">Ingresos habituales:</span>{' '}
            {formatMoney(policy.stableIncome)}
          </p>
          <p>
            <span className="font-semibold text-slate-700">Pagos que ya separaste:</span>{' '}
            {formatMoney(policy.fixedCommitments)}
          </p>
          <p>
            <span className="font-semibold text-slate-700">Dinero para imprevistos:</span>{' '}
            {formatMoney(policy.protectedMargin)}
          </p>
        </div>
        <HelperText className="mt-3" tone="info">
          Protege {formatMoney(policy.committedSavings)} ya apartados y{' '}
          {formatMoney(policy.savingsTarget)} de ahorro objetivo. Crédito disponible:{' '}
          {formatMoney(policy.availableCredit)}; puede impedir un cargo, pero nunca aumenta tu
          capacidad para pagarlo.
        </HelperText>
      </section>

      <Modal
        open={simulatorOpen}
        title="Simula una compra"
        protectUnsavedChanges={false}
        onClose={() => setSimulatorOpen(false)}
      >
        <PurchaseSimulator policy={policy} />
      </Modal>
    </>
  );
}

function CapacityMetric({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="rounded-xl border border-white/90 bg-white/85 p-3.5 shadow-sm">
      <p className="text-[11px] font-bold uppercase tracking-[.07em] text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-bold tracking-tight text-slate-900">{value}</p>
      <p className="mt-1 text-xs leading-4 text-slate-500">{detail}</p>
    </div>
  );
}

function PurchaseSimulator({ policy }: { policy: PurchasePolicy }) {
  const [price, setPrice] = useState('');
  const [months, setMonths] = useState('12');
  const [monthlyPayment, setMonthlyPayment] = useState('');
  const amount = Number(price) || 0;
  const term = Math.max(1, Math.floor(Number(months) || 1));
  const installment = monthlyPayment === '' ? amount / term : Number(monthlyPayment) || 0;
  const financedTotal = installment * term;
  const financeCost = Math.max(0, financedTotal - amount);
  const isCash = term === 1;
  const fitsPurchasePrice = amount <= policy.purchasePriceCap;
  const fitsInstallment = isCash || installment <= policy.newMonthlyMsiCapacity;
  const fitsCashFlow = fitsPurchasePrice && fitsInstallment;
  const fitsBankCredit = amount <= policy.availableCredit;
  const fitsCapacity = fitsCashFlow && fitsBankCredit;
  const isInterestFree = financeCost <= 0.01;
  const hasInput = amount > 0 && installment > 0;
  const maximumForTerm = Math.min(policy.purchasePriceCap, policy.newMonthlyMsiCapacity * term);

  return (
    <div className="space-y-4">
      <p className="rounded-xl bg-slate-50 p-3 text-sm leading-5 text-slate-600">
        Escribe el precio y el pago mensual que muestra la tienda. Si todos los pagos juntos son
        mayores que el precio, la compra sí tiene un costo extra.
      </p>
      <Field
        id="sim-price"
        label="Precio total"
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        value={price}
        onChange={(event) => setPrice(event.target.value)}
        placeholder="0.00"
      />
      <div className="grid grid-cols-2 gap-3">
        <Field
          id="sim-months"
          label="¿En cuántos meses?"
          type="number"
          inputMode="numeric"
          min="1"
          max="60"
          value={months}
          onChange={(event) => setMonths(event.target.value)}
        />
        <Field
          id="sim-monthly"
          label="Pago mensual"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={monthlyPayment}
          onChange={(event) => setMonthlyPayment(event.target.value)}
          placeholder={amount > 0 ? (amount / term).toFixed(2) : 'Se calcula solo'}
          helperText="Déjalo vacío si la tienda confirma que son meses sin intereses."
        />
      </div>

      {hasInput && (
        <div
          className={`rounded-xl border p-3.5 ${!fitsCapacity ? 'border-red-100 bg-red-50' : isInterestFree ? 'border-emerald-100 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
        >
          <p
            className={`text-sm font-semibold ${!fitsCapacity ? 'text-expense' : isInterestFree ? 'text-income' : 'text-amber-800'}`}
          >
            {!fitsPurchasePrice
              ? 'El precio completo supera lo recomendado para tu mes.'
              : !fitsInstallment
                ? 'El pago mensual supera lo recomendado para tus ingresos.'
                : !fitsBankCredit
                  ? 'Tus números permiten pagarla, pero el banco podría rechazarla porque no queda suficiente crédito.'
                  : isInterestFree
                    ? 'Sí cabe dentro de tu tope recomendado.'
                    : 'Puedes pagarla, pero terminarías pagando más que el precio original.'}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-3 text-sm text-slate-700">
            <p>
              <span className="block text-xs text-slate-500">Compromiso mensual</span>
              {formatMoney(installment)}
            </p>
            <p>
              <span className="block text-xs text-slate-500">Total a pagar</span>
              {formatMoney(financedTotal)}
            </p>
          </div>
          {financeCost > 0.01 ? (
            <p className="mt-3 text-xs leading-5 text-expense">
              Comprar a plazos costaría {formatMoney(financeCost)} adicionales. Antes de aceptar,
              pregunta por la tasa, el total final y el CAT, que sirve para comparar el costo de
              distintos créditos.
            </p>
          ) : (
            <p className="mt-3 text-xs leading-5 text-slate-600">
              Si la tienda confirma que son meses sin intereses y pagas a tiempo, no deberían
              cobrarte intereses durante el plazo.
            </p>
          )}
          {!fitsCashFlow && (
            <div className="mt-2 space-y-1 text-xs leading-5 text-slate-600">
              {!fitsPurchasePrice && (
                <p>
                  Aunque pagues poco a poco, la app cuenta el precio completo en el mes de la compra
                  porque la deuda nace completa.
                </p>
              )}
              {!fitsInstallment && (
                <p>
                  La mensualidad sugerida máxima es {formatMoney(policy.monthlyInstallmentCap)}.
                </p>
              )}
              <p>
                Para {term} {term === 1 ? 'mes' : 'meses'}, el precio total prudente es hasta{' '}
                {formatMoney(maximumForTerm)}.
              </p>
            </div>
          )}
          {!fitsBankCredit && (
            <p className="mt-2 text-xs leading-5 text-slate-600">
              Crédito disponible reportado: {formatMoney(policy.availableCredit)}. Esto es una regla
              del banco, separada de tu capacidad de pago.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function ProjectionCard({ cardId }: { cardId: string }) {
  const projQ = useProjection(true);
  // Los planeados viven fuera del ledger de la tarjeta hasta confirmarlos.
  // Los leemos por separado para proyectar el escenario sin convertirlos en
  // deuda o en crédito disponible consumido antes de que realmente ocurran.
  const plannedQ = usePlanned({ status: PlannedMovementStatus.PLANNED });
  const closeCycle = useCloseCycle();
  const [payOpen, setPayOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeNotice, setCloseNotice] = useState<string | null>(null);
  const [statementPayment, setStatementPayment] = useState('');
  if (projQ.isLoading || !projQ.data) return null;
  const p = projQ.data;
  const plannedCardItems = (plannedQ.data ?? []).filter(
    (item) =>
      item.kind === PlannedMovementKind.EXPENSE &&
      item.sourceKind === RecurringSourceKind.CARD &&
      item.cardId === cardId &&
      isWithinCycle(item.expectedDate, {
        start: new Date(p.cycle.start),
        cutoff: new Date(p.cycle.cutoff),
        paymentDue: new Date(p.cycle.paymentDue),
        daysToCutoff: p.cycle.daysToCutoff,
        daysToPayment: p.cycle.daysToPayment,
      }),
  );
  const plannedCardTotal = plannedCardItems.reduce((sum, item) => sum + item.amount, 0);
  const projectedWithPlanned = p.total + plannedCardTotal;
  const statementAmount = Number(statementPayment);
  const validStatementAmount = Number.isFinite(statementAmount) && statementAmount > 0;

  return (
    <section className="ui-card mb-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-[.08em] text-slate-600">
            Próximo pago
          </p>
          <h2 className="mt-1 text-sm font-semibold text-slate-700">
            Pago estimado para no generar intereses
          </h2>
        </div>
        <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
          Antes del {formatDate(p.cycle.paymentDue)}
        </span>
      </div>
      <p className="mt-1 text-3xl font-bold text-slate-900">{formatMoney(p.total)}</p>
      <p className="mt-1 text-xs leading-5 text-slate-500">
        Faltan {p.cycle.daysToPayment} {p.cycle.daysToPayment === 1 ? 'día' : 'días'} para pagar
        según tu configuración.
      </p>
      <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-slate-600">Compras de contado del ciclo</p>
          <p className="font-medium text-slate-700">{formatMoney(p.cashInCycle)}</p>
        </div>
        <div>
          <p className="text-slate-600">Compras a meses</p>
          <p className="font-medium text-slate-700">{formatMoney(p.msiInstallments)}</p>
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-500">
        Corte: {formatDate(p.cycle.cutoff)} · pago: {formatDate(p.cycle.paymentDue)}
      </p>
      {plannedCardItems.length > 0 && (
        <section
          className="mt-4 rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 sm:p-4"
          aria-label="Escenario con cargos planeados de tarjeta"
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[.08em] text-amber-800">
                Escenario
              </p>
              <h3 className="mt-0.5 text-sm font-semibold text-slate-800">
                Cierre estimado si confirmas planeados
              </h3>
            </div>
            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">
              {plannedCardItems.length} {plannedCardItems.length === 1 ? 'cargo' : 'cargos'}
            </span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
            <ScenarioMetric label="Registrado" value={formatMoney(p.total)} />
            <ScenarioMetric
              label="Planeado"
              value={`+${formatMoney(plannedCardTotal)}`}
              tone="amber"
            />
            <ScenarioMetric
              label="Si se confirma"
              value={formatMoney(projectedWithPlanned)}
              className="col-span-2 sm:col-span-1"
              tone="strong"
            />
          </div>
          <ul className="mt-3 divide-y divide-amber-200/80 border-t border-amber-200/80">
            {plannedCardItems.map((item) => (
              <li
                key={item.id}
                className="flex min-w-0 items-center justify-between gap-3 py-2 text-xs"
              >
                <span className="min-w-0 truncate text-slate-700">
                  {item.note ?? 'Compra planeada'} · {formatDate(item.expectedDate)}
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-slate-800">
                  {formatMoney(item.amount)}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs leading-5 text-amber-900">
            Aún no cambia tu deuda ni el crédito disponible. Sólo se suma al cierre estimado porque
            entraría en este ciclo si lo confirmas.
          </p>
        </section>
      )}
      <HelperText className="mt-3" tone="info">
        Es un cálculo estimado con las compras registradas aquí. Confirma el monto final en el
        estado de cuenta de tu banco. Si falta una compra anterior a meses, este cálculo puede
        quedar más bajo de lo real. Pagar completo el monto para no generar intereses evita que te
        cobren intereses.
      </HelperText>
      <div className="mt-3 flex items-center gap-4">
        <button
          onClick={() => setPayOpen(true)}
          className="tap-target rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white"
        >
          Pagar tarjeta
        </button>
        <Tooltip
          content="Avanza las mensualidades del corte después de registrar el pago; no mueve dinero"
          align="start"
        >
          <button
            onClick={() => {
              setStatementPayment('');
              setCloseOpen(true);
            }}
            className="text-xs font-medium text-savings"
          >
            Confirmar pago del corte
          </button>
        </Tooltip>
      </div>
      {closeNotice && <p className="mt-2 text-xs font-medium text-savings">{closeNotice}</p>}

      <Modal open={payOpen} title="Pagar tarjeta" onClose={() => setPayOpen(false)}>
        <PayCardForm suggested={p.total} onDone={() => setPayOpen(false)} />
      </Modal>
      <Modal open={closeOpen} title="Confirmar pago del corte" onClose={() => setCloseOpen(false)}>
        <div className="space-y-4">
          <p className="text-sm leading-6 text-slate-600">
            Copia el “pago para no generar intereses” exacto de tu estado de cuenta. Confirma este
            paso sólo después de registrar el pago en la app. Así avanzará una mensualidad de las
            compras que ya estaban en ese corte; si vuelves a tocar el botón, no avanzarán otra vez.
          </p>
          <div className="rounded-xl border border-amber-100 bg-amber-50/70 p-3 text-sm leading-5 text-slate-700">
            <p className="font-semibold text-slate-800">
              La app estima {formatMoney(p.total)}, pero el banco manda.
            </p>
            <p className="mt-1">
              El estado puede diferir por saldo previo, ajustes, pagos o cargos que aún no
              registraste. Este paso sólo confirma el corte; no crea un pago nuevo.
            </p>
          </div>
          <Field
            id="statement-payment-for-no-interest"
            label="Pago para no generar intereses del estado de cuenta"
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            required
            value={statementPayment}
            onChange={(event) => setStatementPayment(event.target.value)}
            placeholder={p.total.toFixed(2)}
            helperText="Verifica este monto en la app o el estado de cuenta de tu banco. Primero registra aquí los pagos que lo cubren."
          />
          {closeCycle.isError && (
            <p role="alert" className="text-sm text-expense">
              {getApiErrorMessage(closeCycle.error)}
            </p>
          )}
          <div className="modal-action-bar flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="button button-secondary w-full sm:w-auto"
              onClick={() => setCloseOpen(false)}
              disabled={closeCycle.isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="button button-primary w-full sm:w-auto"
              disabled={closeCycle.isPending || !validStatementAmount}
              onClick={() =>
                closeCycle.mutate(
                  { statementPaymentForNoInterest: statementAmount },
                  {
                    onSuccess: (result) => {
                      setCloseOpen(false);
                      setCloseNotice(
                        result.noNewCutoff
                          ? 'Aún no hay un corte nuevo para confirmar. Vuelve después de la próxima fecha de corte.'
                          : result.alreadyClosed
                            ? 'Ese corte ya estaba registrado; tus mensualidades no cambiaron.'
                            : result.advanced > 0
                              ? `Pago del corte confirmado con ${formatMoney(result.statementPaymentForNoInterest ?? statementAmount)}: avanzaron ${result.advanced} compras a meses.`
                              : 'Pago confirmado; no había mensualidades por avanzar.',
                      );
                    },
                  },
                )
              }
            >
              {closeCycle.isPending ? 'Confirmando…' : 'Confirmar pago'}
            </button>
          </div>
        </div>
      </Modal>
    </section>
  );
}

function ScenarioMetric({
  label,
  value,
  tone = 'default',
  className = '',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'amber' | 'strong';
  className?: string;
}) {
  const valueClass =
    tone === 'strong' ? 'text-slate-950' : tone === 'amber' ? 'text-amber-900' : 'text-slate-700';
  const cardClass =
    tone === 'strong' ? 'border-amber-300 bg-white' : 'border-amber-200/80 bg-white/60';
  return (
    <div className={`min-w-0 rounded-lg border px-3 py-2.5 ${cardClass} ${className}`}>
      <p className="text-[10px] font-bold uppercase tracking-[.07em] text-slate-500">{label}</p>
      <p className={`mt-1 truncate text-base font-bold tracking-tight tabular-nums ${valueClass}`}>
        {value}
      </p>
    </div>
  );
}

/**
 * Registra un pago a la tarjeta: descuenta de la cuenta elegida y NO cuenta
 * como gasto variable, porque liquida compras ya registradas; volverlo a sumar
 * ocultaría cuánto margen real queda para compras nuevas.
 */
function PayCardForm({ suggested, onDone }: { suggested: number; onDone: () => void }) {
  const accountsQ = useAccounts();
  const pay = useCreatePayment();
  const accounts = accountsQ.data ?? [];
  const [accountId, setAccountId] = useState('');
  const [amount, setAmount] = useState(String(Math.round(suggested * 100) / 100));
  const [date, setDate] = useState(toDateInputValue(new Date()));
  const [note, setNote] = useState('');
  const numericAmount = Number(amount);
  const amountIsValid = amount.trim() !== '' && Number.isFinite(numericAmount) && numericAmount > 0;
  const dateIsValid = isValidDateInput(date) && date <= toDateInputValue();
  // Se conserva mientras el formulario siga abierto: un doble toque o un
  // reenvío tras perder la respuesta representa el mismo intento.
  const [idempotencyKey] = useState(createCardAttemptKey);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const resolved = accountId || accounts[0]?.id;
    if (!resolved || !amountIsValid || !dateIsValid) return;
    pay.mutate(
      {
        accountId: resolved,
        amount: numericAmount,
        date,
        note: note || undefined,
        idempotencyKey,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
        El pago sale de la cuenta que elijas y reduce la deuda. No se cuenta como un gasto nuevo:
        las compras originales ya están registradas; sólo las mensualidades de compras anteriores se
        reservan como pagos del mes.
      </p>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Pagar desde</span>
        <select
          className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          required
        >
          <option value="" disabled>
            Elige cuenta
          </option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </label>
      <Field
        id="pay-amount"
        label="Monto"
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0.01"
        required
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <DatePicker
        id="pay-date"
        label="Fecha"
        required
        max={toDateInputValue()}
        value={date}
        onChange={setDate}
      />
      {!dateIsValid && (
        <HelperText tone="danger" role="alert">
          Elige una fecha de pago válida y que no esté en el futuro.
        </HelperText>
      )}
      <Field
        id="pay-note"
        label="Nota (opcional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {pay.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(pay.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={pay.isPending || !amountIsValid || !dateIsValid}
          className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
        >
          {pay.isPending ? 'Guardando…' : 'Registrar pago'}
        </button>
      </div>
    </form>
  );
}

/** Historial de pagos a la tarjeta. */
function PaymentsSection() {
  const paymentsQ = usePayments();
  const del = useDeletePayment();
  const [paymentToDelete, setPaymentToDelete] = useState<CardPayment | null>(null);
  const accountsQ = useAccounts();
  const accountName = useMemo(() => {
    const map = new Map<string, string>();
    (accountsQ.data ?? []).forEach((a) => map.set(a.id, a.name));
    return map;
  }, [accountsQ.data]);
  const payments = paymentsQ.data ?? [];
  if (payments.length === 0) return null;

  return (
    <section className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h2 className="mb-2 text-sm font-semibold text-slate-700">Pagos a la tarjeta</h2>
      <ul className="space-y-2">
        {payments.map((p) => (
          <li key={p.id} className="flex items-center justify-between text-sm">
            <div className="min-w-0">
              <p className="truncate text-slate-700">
                {accountName.get(p.accountId) ?? 'Cuenta'}
                {p.note ? ` · ${p.note}` : ''}
              </p>
              <p className="text-xs text-slate-600">{formatDate(p.date)}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-semibold text-slate-700">{formatMoney(p.amount)}</span>
              <button
                onClick={() => setPaymentToDelete(p)}
                className="tap-target flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-red-50 hover:text-expense"
                aria-label="Eliminar"
              >
                ×
              </button>
            </div>
          </li>
        ))}
      </ul>
      <ConfirmDialog
        open={paymentToDelete !== null}
        title="¿Eliminar pago de tarjeta?"
        description="El pago se revertirá: volverá dinero a la cuenta y aumentará la deuda registrada de la tarjeta."
        itemLabel={
          paymentToDelete
            ? `${formatMoney(paymentToDelete.amount)} · ${formatDate(paymentToDelete.date)}`
            : undefined
        }
        pending={del.isPending}
        error={del.isError ? getApiErrorMessage(del.error) : undefined}
        onClose={() => setPaymentToDelete(null)}
        onConfirm={() =>
          paymentToDelete &&
          del.mutate(paymentToDelete.id, { onSuccess: () => setPaymentToDelete(null) })
        }
      />
    </section>
  );
}

function InstallmentsSection({ data, loading }: { data?: InstallmentsResponse; loading: boolean }) {
  if (loading || !data) return null;
  const { charges, totalMsiDebt } = data;
  if (charges.length === 0) return null;

  return (
    <section className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">Compras a meses sin intereses</h2>
        <span className="text-sm text-slate-500">
          Falta por pagar:{' '}
          <span className="font-semibold text-expense">{formatMoney(totalMsiDebt)}</span>
        </span>
      </div>
      <ul className="mt-3 space-y-4">
        {charges.map((c) => (
          <li key={c.id}>
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-800">{c.note ?? 'Compra'}</span>
              <span className="text-slate-500">
                vas {c.msiPaid}/{c.msiMonths}
              </span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-savings"
                style={{ width: `${Math.round(c.progress * 100)}%` }}
              />
            </div>
            <div className="mt-1 flex justify-between text-xs text-slate-600">
              <span>{formatMoney(c.monthlyAmount)}/mes</span>
              <span>Falta {formatMoney(c.remainingBalance)}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ChargesSection({ card }: { card: CreditCard }) {
  const chargesQ = useCharges();
  const update = useUpdateCharge();
  const del = useDeleteCharge();
  const [chargeToDelete, setChargeToDelete] = useState<CcCharge | null>(null);
  const [editingCharge, setEditingCharge] = useState<CcCharge | null>(null);
  const [view, setView] = useState<'cycle' | 'history'>('cycle');
  const [month, setMonth] = useState(currentYearMonth);
  const [allTime, setAllTime] = useState(false);
  const charges = chargesQ.data ?? [];
  const visibleCharges = useMemo(
    () =>
      charges.filter((charge) => {
        if (view === 'cycle') return isInCurrentCycle(charge, card.cutoffDay);
        return allTime || charge.purchaseDate.slice(0, 7) === month;
      }),
    [allTime, card.cutoffDay, charges, month, view],
  );
  if (charges.length === 0) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Compras con tarjeta</h2>
          <p className="text-xs text-slate-600">
            {view === 'cycle'
              ? 'Compras del ciclo vigente'
              : allTime
                ? 'Todo el historial'
                : `Historial de ${month}`}
          </p>
        </div>
        <div className="inline-flex gap-1 rounded-xl bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setView('cycle')}
            className={`h-9 rounded-lg px-3 text-sm font-semibold transition ${view === 'cycle' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
          >
            Ciclo actual
          </button>
          <button
            type="button"
            onClick={() => setView('history')}
            className={`h-9 rounded-lg px-3 text-sm font-semibold transition ${view === 'history' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
          >
            Historial
          </button>
        </div>
      </div>
      {view === 'history' && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <MonthPicker
            value={month}
            allTime={allTime}
            onChange={(nextMonth) => {
              setMonth(nextMonth);
              setAllTime(false);
            }}
            onAllTime={() => setAllTime(true)}
          />
        </div>
      )}
      {visibleCharges.length === 0 && (
        <p className="py-8 text-center text-sm text-slate-600">No hay cargos para este período.</p>
      )}
      <ul className="divide-y divide-slate-100">
        {visibleCharges.map((c) => {
          const accountingLocked = isChargeAccountingLocked(c, card);
          return (
            <li key={c.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
              <div className="min-w-0">
                <p className="truncate text-slate-800">{c.note ?? 'Cargo'}</p>
                <p className="text-xs text-slate-600">
                  {formatDate(c.purchaseDate)} ·{' '}
                  {c.msiMonths === 1 ? 'Contado' : `${c.msiMonths} meses sin intereses`}
                  {c.captureMode === 'historical' && ' · compra anterior'}
                  {c.recurringExpenseId && ' · cargo fijo'}
                  {accountingLocked && ' · corte confirmado'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className="mr-1 font-medium text-slate-700">
                  {formatMoney(c.totalAmount)}
                </span>
                {c.recurringExpenseId ? (
                  <Tooltip
                    content="Cambiar este mes o los próximos desde Movimientos fijos"
                    align="end"
                  >
                    <Link
                      to={`/gastos-fijos?ajustar=${encodeURIComponent(c.recurringExpenseId)}&mes=${c.recurringOccurrenceMonth ?? c.purchaseDate.slice(0, 7)}`}
                      className="tap-target inline-flex h-9 items-center px-1 text-xs font-semibold text-savings hover:text-blue-700"
                      aria-label={`Cambiar monto del cargo fijo ${c.note ?? ''}`.trim()}
                    >
                      Fijo
                    </Link>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip
                      content={accountingLocked ? 'Corregir nota o categoría' : 'Editar compra'}
                      align="end"
                    >
                      <button
                        onClick={() => setEditingCharge(c)}
                        className="tap-target flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-800"
                        aria-label={`Editar ${c.note ?? 'cargo'}`}
                      >
                        <svg
                          className="h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <path d="m14.5 5.5 4 4M4 20l4.2-1 10.4-10.4a2.8 2.8 0 0 0-4-4L4.2 15z" />
                        </svg>
                      </button>
                    </Tooltip>
                    {!accountingLocked && (
                      <Tooltip content="Eliminar compra" align="end">
                        <button
                          onClick={() => setChargeToDelete(c)}
                          className="tap-target flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-red-50 hover:text-expense"
                          aria-label="Eliminar cargo"
                        >
                          ×
                        </button>
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      <Modal
        open={editingCharge !== null}
        title="Editar compra con tarjeta"
        onClose={() => setEditingCharge(null)}
      >
        {editingCharge && (
          <EditChargeForm
            charge={editingCharge}
            card={card}
            pending={update.isPending}
            error={update.error}
            onSubmit={(body) =>
              update.mutate(
                { id: editingCharge.id, body },
                { onSuccess: () => setEditingCharge(null) },
              )
            }
          />
        )}
      </Modal>
      <ConfirmDialog
        open={chargeToDelete !== null}
        title="¿Eliminar compra de tarjeta?"
        description={
          chargeToDelete?.balanceImpactAmount === 0
            ? 'Se quitará del historial y de tus mensualidades. Tu deuda total no cambiará porque esta compra ya estaba incluida cuando la registraste.'
            : 'Se quitará del historial y se descontará de tu deuda exactamente el monto que se agregó al guardarla. Úsalo sólo si la registraste por error.'
        }
        itemLabel={
          chargeToDelete
            ? `${chargeToDelete.note ?? 'Compra'} · ${formatMoney(chargeToDelete.totalAmount)}`
            : undefined
        }
        pending={del.isPending}
        error={del.isError ? getApiErrorMessage(del.error) : undefined}
        onClose={() => setChargeToDelete(null)}
        onConfirm={() =>
          chargeToDelete &&
          del.mutate(chargeToDelete.id, { onSuccess: () => setChargeToDelete(null) })
        }
      />
    </section>
  );
}

function isChargeAccountingLocked(charge: CcCharge, card: CreditCard): boolean {
  if (typeof charge.accountingLocked === 'boolean') return charge.accountingLocked;
  if (charge.msiPaid > 0) return true;
  if (!card.lastMsiCycleClosedAt) return false;
  return new Date(charge.purchaseDate).getTime() <= new Date(card.lastMsiCycleClosedAt).getTime();
}

/** Corrige datos de una compra sin abrir la posibilidad de reescribir un corte ya conciliado. */
function EditChargeForm({
  charge,
  card,
  pending,
  error,
  onSubmit,
}: {
  charge: CcCharge;
  card: CreditCard;
  pending: boolean;
  error: unknown;
  onSubmit: (body: {
    totalAmount?: number;
    purchaseDate?: string;
    msiMonths?: number;
    currentInstallment?: number;
    currentInstallmentPaid?: boolean;
    balanceTreatment?: HistoricalBalanceTreatment;
    categoryId?: string | null;
    note?: string;
  }) => void;
}) {
  const categoriesQ = useCategories();
  const locked = isChargeAccountingLocked(charge, card);
  const [note, setNote] = useState(charge.note ?? '');
  const [amount, setAmount] = useState(String(charge.totalAmount));
  const [months, setMonths] = useState(String(charge.msiMonths));
  const [date, setDate] = useState(toDateInputValue(charge.purchaseDate));
  const [categoryId, setCategoryId] = useState(charge.categoryId ?? '');
  const [historicalPaidCount, setHistoricalPaidCount] = useState(String(charge.msiPaid));
  const [balanceTreatment, setBalanceTreatment] = useState<HistoricalBalanceTreatment>(
    charge.balanceTreatment === 'addOutstanding' ? 'addOutstanding' : 'alreadyIncluded',
  );
  const expenseCategories = useMemo(
    () => (categoriesQ.data ?? []).filter((category) => category.kind === CategoryKind.EXPENSE),
    [categoriesQ.data],
  );
  const isHistorical = charge.captureMode === 'historical';
  const nextAmount = Number(amount) || 0;
  const nextMonths = Math.max(1, Math.floor(Number(months) || 1));
  const paidCount = Math.max(0, Math.floor(Number(historicalPaidCount) || 0));
  const historicalProgressValid =
    !isHistorical || (nextMonths >= 2 && paidCount >= 0 && paidCount < nextMonths);
  const dateIsValid = locked || (isValidDateInput(date) && date <= toDateInputValue());
  const canonicalCurrentInstallment =
    paidCount >= nextMonths ? nextMonths : Math.min(nextMonths, paidCount + 1);
  const canonicalCurrentInstallmentPaid = paidCount >= nextMonths;
  const nextHistoricalSummary = summarizeHistoricalMsi(
    nextAmount,
    nextMonths,
    canonicalCurrentInstallment,
    canonicalCurrentInstallmentPaid,
  );
  const previousImpact = charge.balanceImpactAmount ?? charge.totalAmount;
  const nextImpact = isHistorical
    ? balanceTreatment === 'addOutstanding'
      ? nextHistoricalSummary.remainingBalance
      : 0
    : nextAmount;
  const resultingDebt = card.balance - previousImpact + nextImpact;
  const debtWouldBeNegative = resultingDebt < -0.005;
  const debtWouldExceedLimit = resultingDebt > card.creditLimit + 0.005;
  const debtChangeInvalid = !locked && (debtWouldBeNegative || debtWouldExceedLimit);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!historicalProgressValid || !dateIsValid) return;
    onSubmit({
      note: note.trim() || undefined,
      categoryId: categoryId || null,
      ...(locked
        ? {}
        : {
            totalAmount: Number(amount),
            purchaseDate: date,
            msiMonths: nextMonths,
            ...(isHistorical
              ? {
                  currentInstallment: canonicalCurrentInstallment,
                  currentInstallmentPaid: canonicalCurrentInstallmentPaid,
                  balanceTreatment,
                }
              : {}),
          }),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {locked ? (
        <HelperText tone="info">
          Esta compra ya pasó por un corte dentro de la app. Puedes corregir su nombre o categoría,
          pero los montos y mensualidades quedan protegidos para no cambiar tu historial.
        </HelperText>
      ) : (
        <p className="rounded-xl bg-slate-50 p-3 text-sm leading-5 text-slate-600">
          Puedes corregir esta compra porque la app todavía no ha avanzado ninguna de sus
          mensualidades.
        </p>
      )}
      <Field
        id={`edit-charge-note-${charge.id}`}
        label="¿Qué compraste?"
        required
        maxLength={120}
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Categoría</span>
        <select
          className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base text-slate-900"
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
        >
          <option value="">Sin categoría</option>
          {expenseCategories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.icon ? `${category.icon} ` : ''}
              {category.name}
            </option>
          ))}
        </select>
      </label>
      {!locked && (
        <>
          <Field
            id={`edit-charge-amount-${charge.id}`}
            label={isHistorical ? 'Precio total original' : 'Monto total'}
            type="number"
            inputMode="decimal"
            min="0.01"
            step="0.01"
            required
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              id={`edit-charge-months-${charge.id}`}
              label={isHistorical ? '¿A cuántos meses fue?' : 'Número de mensualidades'}
              type="number"
              inputMode="numeric"
              min={isHistorical ? '2' : '1'}
              required
              value={months}
              onChange={(event) => setMonths(event.target.value)}
            />
            <DatePicker
              id={`edit-charge-date-${charge.id}`}
              label="Fecha de compra"
              required
              value={date}
              onChange={setDate}
              max={toDateInputValue()}
            />
          </div>
          {!dateIsValid && (
            <HelperText tone="danger" role="alert">
              Elige una fecha de compra válida.
            </HelperText>
          )}
          {isHistorical && (
            <div className="space-y-3 rounded-2xl border border-blue-100 bg-blue-50/60 p-3.5">
              <Field
                id={`edit-charge-paid-${charge.id}`}
                label="¿Cuántas mensualidades ya pagaste?"
                type="number"
                inputMode="numeric"
                min="0"
                max={String(Math.max(0, nextMonths - 1))}
                required
                value={historicalPaidCount}
                onChange={(event) => setHistoricalPaidCount(event.target.value)}
                helperText="Cuenta sólo las que ya quedaron pagadas. No incluyas la que todavía debes."
              />
              {!historicalProgressValid && (
                <HelperText tone="danger" role="alert">
                  Esta sección es para compras que todavía tienen pagos pendientes. Escribe un
                  número entre 0 y {Math.max(0, nextMonths - 1)}.
                </HelperText>
              )}
              <fieldset>
                <legend className="mb-1 text-sm font-semibold text-slate-800">
                  ¿Lo que falta ya estaba incluido en tu deuda?
                </legend>
                <p className="mb-2 text-xs leading-5 text-slate-600">
                  Cambiar esta opción ajustará sólo la diferencia; nunca volverá a sumar el precio
                  completo.
                </p>
                <div className="space-y-2">
                  <label className="flex min-h-12 cursor-pointer items-center gap-2 rounded-xl border border-blue-100 bg-white px-3 py-2.5 text-sm">
                    <input
                      type="radio"
                      name={`edit-balance-treatment-${charge.id}`}
                      checked={balanceTreatment === 'alreadyIncluded'}
                      onChange={() => setBalanceTreatment('alreadyIncluded')}
                    />
                    Sí, ya estaba incluido
                  </label>
                  <label className="flex min-h-12 cursor-pointer items-center gap-2 rounded-xl border border-blue-100 bg-white px-3 py-2.5 text-sm">
                    <input
                      type="radio"
                      name={`edit-balance-treatment-${charge.id}`}
                      checked={balanceTreatment === 'addOutstanding'}
                      onChange={() => setBalanceTreatment('addOutstanding')}
                    />
                    No, agrega lo que falta por pagar
                  </label>
                </div>
              </fieldset>
              {historicalProgressValid && nextAmount > 0 && (
                <div className="rounded-xl border border-blue-100 bg-white p-3 text-sm text-slate-700">
                  <p>
                    Quedarán {nextHistoricalSummary.monthsRemaining}{' '}
                    {nextHistoricalSummary.monthsRemaining === 1 ? 'pago' : 'pagos'} por un total
                    aproximado de{' '}
                    <strong>
                      {formatMoney(nextHistoricalSummary.remainingBalance, card.currency)}
                    </strong>
                    .
                  </p>
                  <div className="mt-2 border-t border-slate-100 pt-2">
                    <div className="flex justify-between gap-3">
                      <span>Deuda antes</span>
                      <span>{formatMoney(card.balance, card.currency)}</span>
                    </div>
                    <div className="mt-1 flex justify-between gap-3 font-semibold text-slate-900">
                      <span>Deuda después</span>
                      <span>{formatMoney(resultingDebt, card.currency)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {debtChangeInvalid && (
        <HelperText tone="danger" role="alert">
          {debtWouldBeNegative
            ? 'Este cambio quitaría más deuda de la que hoy tienes registrada. Conserva los montos y corrige sólo el nombre o la categoría.'
            : 'Este cambio dejaría la deuda por encima del límite de tu tarjeta. Revisa los montos antes de guardar.'}
        </HelperText>
      )}
      {Boolean(error) && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={
            pending || !note.trim() || !historicalProgressValid || !dateIsValid || debtChangeInvalid
          }
          className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
        >
          {pending ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  );
}

function CardForm({ card, onDone }: { card?: CreditCard; onDone?: () => void } = {}) {
  const upsert = useUpsertCard();
  const [institution, setInstitution] = useState(card?.institution ?? '');
  const [name, setName] = useState(card?.name ?? '');
  const [balance, setBalance] = useState(card ? String(card.balance) : '');
  const [creditLimit, setCreditLimit] = useState(card ? String(card.creditLimit) : '');
  const [cutoffDay, setCutoffDay] = useState(card ? String(card.cutoffDay) : '15');
  const [paymentDay, setPaymentDay] = useState(card ? String(card.paymentDay) : '5');
  const [currency, setCurrency] = useState(card?.currency ?? currentCurrency());
  const debt = Number(balance) || 0;
  const limit = Number(creditLimit) || 0;
  const invalidLimit = limit > 0 && debt > limit;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (invalidLimit) return;
    upsert.mutate(
      {
        institution: card ? institution.trim() : institution.trim() || undefined,
        name: name.trim(),
        currency,
        ...(card && Math.abs(debt - card.balance) <= 0.005 ? {} : { balance: debt }),
        creditLimit: limit,
        cutoffDay: Number(cutoffDay),
        paymentDay: Number(paymentDay),
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="rounded-xl bg-blue-50 p-3 text-sm leading-6 text-blue-900">
        Ten a la mano la app o el estado de cuenta de tu banco. Usaremos estos datos para decirte
        cuánto debes y qué pagos vienen.
      </p>
      <Field
        id="cc-name"
        label="¿Qué nombre le quieres poner?"
        required
        maxLength={60}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Ej. Tarjeta principal o Tarjeta de viajes"
        helperText="Elige un nombre corto que puedas reconocer fácilmente."
      />
      <Field
        id="cc-balance"
        label="¿Cuánto debes hoy?"
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        required
        value={balance}
        onChange={(e) => setBalance(e.target.value)}
        placeholder="0.00"
        helperText={
          card
            ? 'Cámbialo sólo si necesitas corregir el saldo. Las compras y pagos nuevos lo actualizan automáticamente.'
            : 'Copia el saldo actual que muestra tu banco. Si no debes nada, escribe 0.'
        }
      />
      <Field
        id="cc-limit"
        label="¿De cuánto es tu límite de crédito?"
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        required
        value={creditLimit}
        onChange={(e) => setCreditLimit(e.target.value)}
        placeholder="0.00"
        helperText="Es el monto máximo que tu banco te permite usar; no significa que convenga gastarlo completo."
      />
      {invalidLimit && (
        <HelperText tone="danger" role="alert">
          La deuda no puede ser mayor que el límite. Revisa ambos montos en la app de tu banco.
        </HelperText>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          id="cc-cutoff"
          label="¿Qué día es tu corte?"
          type="number"
          inputMode="numeric"
          min="1"
          max="31"
          required
          value={cutoffDay}
          onChange={(e) => setCutoffDay(e.target.value)}
          helperText="Ese día el banco cierra las compras del periodo."
        />
        <Field
          id="cc-payment"
          label="¿Qué día vence tu pago?"
          type="number"
          inputMode="numeric"
          min="1"
          max="31"
          required
          value={paymentDay}
          onChange={(e) => setPaymentDay(e.target.value)}
          helperText="Es la fecha límite para pagar."
        />
      </div>
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Moneda</span>
        <select
          value={currency}
          onChange={(e) => setCurrency(e.target.value as typeof currency)}
          disabled={Boolean(card)}
          className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base"
        >
          <option value="MXN">Pesos mexicanos (MXN)</option>
          <option value="USD">Dólares estadounidenses (USD)</option>
          <option value="CAD">Dólares canadienses (CAD)</option>
        </select>
        {card && (
          <span className="mt-1.5 block text-xs leading-5 text-slate-500">
            La moneda no se puede cambiar después de crear la tarjeta.
          </span>
        )}
      </label>
      <Field
        id="cc-institution"
        label="Banco o institución (opcional)"
        maxLength={80}
        value={institution}
        onChange={(event) => setInstitution(event.target.value)}
        placeholder="Ej. Santander, Nu o una cooperativa"
        helperText="Es quien emitió la tarjeta. Puedes escribir cualquier banco o institución."
      />
      {upsert.isError && (
        <p role="alert" className="text-sm text-expense">
          {getApiErrorMessage(upsert.error)}
        </p>
      )}
      <div className="modal-action-bar">
        <button
          type="submit"
          disabled={upsert.isPending || invalidLimit || !name.trim()}
          className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60"
        >
          {upsert.isPending ? 'Guardando…' : card ? 'Guardar cambios' : 'Guardar tarjeta'}
        </button>
      </div>
    </form>
  );
}

function NewChargeForm({
  card,
  policy,
  initialMode,
  onDone,
}: {
  card: CreditCard;
  policy?: PurchasePolicy;
  initialMode: CcChargeCaptureMode;
  onDone: () => void;
}) {
  const create = useCreateCharge();
  const categoriesQ = useCategories();
  const [captureMode, setCaptureMode] = useState<CcChargeCaptureMode>(initialMode);
  const [note, setNote] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [msiMonths, setMsiMonths] = useState(initialMode === 'historical' ? '12' : '1');
  const [purchaseDate, setPurchaseDate] = useState(toDateInputValue());
  const [categoryId, setCategoryId] = useState('');
  const [exceptionalPurchase, setExceptionalPurchase] = useState(false);
  const [currentInstallment, setCurrentInstallment] = useState('1');
  const [currentInstallmentPaid, setCurrentInstallmentPaid] = useState(false);
  const [balanceTreatment, setBalanceTreatment] =
    useState<HistoricalBalanceTreatment>('alreadyIncluded');
  const [idempotencyKey] = useState(createCardAttemptKey);
  const expenseCategories = useMemo(
    () => (categoriesQ.data ?? []).filter((category) => category.kind === CategoryKind.EXPENSE),
    [categoriesQ.data],
  );
  const amount = Number(totalAmount) || 0;
  const term = Math.max(1, Math.floor(Number(msiMonths) || 1));
  const installmentNumber = Math.max(1, Math.floor(Number(currentInstallment) || 1));
  const monthlyCost = amount / term;
  const historical = summarizeHistoricalMsi(
    amount,
    term,
    installmentNumber,
    currentInstallmentPaid,
  );
  const isHistorical = captureMode === 'historical';
  const historicalAlreadyFinished =
    isHistorical && installmentNumber === term && currentInstallmentPaid;
  const historicalFieldsValid =
    !isHistorical ||
    (term >= 2 &&
      installmentNumber >= 1 &&
      installmentNumber <= term &&
      !historicalAlreadyFinished);
  const purchaseDateValid = isValidDateInput(purchaseDate) && purchaseDate <= toDateInputValue();
  const balanceImpact = isHistorical
    ? balanceTreatment === 'addOutstanding'
      ? historical.remainingBalance
      : 0
    : amount;
  const availableCredit = policy?.availableCredit ?? Math.max(0, card.creditLimit - card.balance);
  const resultingDebt = card.balance + balanceImpact;
  const exceedsPurchasePriceCap = Boolean(
    policy && !isHistorical && amount > 0 && amount > policy.purchasePriceCap,
  );
  const exceedsMonthlyCap = Boolean(
    policy && !isHistorical && amount > 0 && term > 1 && monthlyCost > policy.newMonthlyMsiCapacity,
  );
  const exceedsCredit = balanceImpact > availableCredit + 0.005;
  const requiresFlowException = exceedsPurchasePriceCap || exceedsMonthlyCap;
  const blockedByCredit = exceedsCredit;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (
      amount <= 0 ||
      !note.trim() ||
      !purchaseDateValid ||
      !historicalFieldsValid ||
      blockedByCredit ||
      (requiresFlowException && !exceptionalPurchase)
    )
      return;
    create.mutate(
      {
        note: note.trim(),
        totalAmount: amount,
        msiMonths: term,
        purchaseDate,
        categoryId: categoryId || undefined,
        captureMode,
        ...(isHistorical
          ? {
              currentInstallment: installmentNumber,
              currentInstallmentPaid,
              balanceTreatment,
            }
          : {}),
        idempotencyKey,
      },
      { onSuccess: onDone },
    );
  };

  return (
    <form onSubmit={onSubmit} className="grid gap-4 lg:grid-cols-2 lg:items-start">
      <fieldset className="lg:col-span-2">
        <legend className="mb-2 text-sm font-semibold text-slate-800">
          ¿Qué quieres registrar?
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          <label
            className={`cursor-pointer rounded-xl border p-3 transition ${
              captureMode === 'new'
                ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <span className="flex items-start gap-2">
              <input
                type="radio"
                name="charge-capture-mode"
                value="new"
                checked={captureMode === 'new'}
                onChange={() => setCaptureMode('new')}
                className="mt-1 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-semibold text-slate-900">Compra nueva</span>
                <span className="mt-0.5 block text-xs leading-5 text-slate-500">
                  La acabas de hacer o todavía no estaba registrada.
                </span>
              </span>
            </span>
          </label>
          <label
            className={`cursor-pointer rounded-xl border p-3 transition ${
              isHistorical
                ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900'
                : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
          >
            <span className="flex items-start gap-2">
              <input
                type="radio"
                name="charge-capture-mode"
                value="historical"
                checked={isHistorical}
                onChange={() => {
                  setCaptureMode('historical');
                  if (term < 2) setMsiMonths('12');
                }}
                className="mt-1 h-4 w-4"
              />
              <span>
                <span className="block text-sm font-semibold text-slate-900">
                  Compra a meses que ya pagabas
                </span>
                <span className="mt-0.5 block text-xs leading-5 text-slate-500">
                  Es un plan de meses sin intereses que empezó antes.
                </span>
              </span>
            </span>
          </label>
        </div>
      </fieldset>
      <Field
        id="ch-amount"
        label={isHistorical ? '¿Cuál fue el precio total de la compra?' : '¿Cuánto costó en total?'}
        type="number"
        inputMode="decimal"
        min="0.01"
        step="0.01"
        required
        value={totalAmount}
        onChange={(e) => setTotalAmount(e.target.value)}
        placeholder="0.00"
        helperText={
          isHistorical
            ? 'Escribe el precio original, no sólo lo que falta por pagar.'
            : term > 1 && amount > 0
              ? `Serían aproximadamente ${formatMoney(monthlyCost, card.currency)} al mes.`
              : undefined
        }
      />
      <Field
        id="ch-note"
        label="¿Qué compraste?"
        required
        maxLength={120}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Ej. celular, supermercado o vuelo"
        helperText="Usa un nombre que puedas reconocer después."
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:col-span-2">
        <Field
          id="ch-msi"
          label={isHistorical ? '¿A cuántos meses fue?' : '¿En cuántas mensualidades lo pagarás?'}
          type="number"
          inputMode="numeric"
          min={isHistorical ? '2' : '1'}
          required
          value={msiMonths}
          onChange={(e) => setMsiMonths(e.target.value)}
          helperText={
            isHistorical
              ? 'Ej. 12 para una compra a 12 meses sin intereses.'
              : 'Escribe 1 si la pagarás de contado.'
          }
        />
        <DatePicker
          id="ch-date"
          label={isHistorical ? '¿Cuándo hiciste la compra?' : 'Fecha de compra'}
          value={purchaseDate}
          onChange={setPurchaseDate}
          required
          max={toDateInputValue()}
          helperText={
            isHistorical ? 'Elige la fecha original, aunque haya sido hace meses.' : undefined
          }
        />
      </div>
      {!purchaseDateValid && (
        <HelperText tone="danger" role="alert" className="lg:col-span-2">
          Elige una fecha de compra válida para poder guardar.
        </HelperText>
      )}
      {isHistorical && (
        <div className="space-y-4 rounded-2xl border border-blue-100 bg-blue-50/60 p-3.5 sm:p-4 lg:col-span-2 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
          <div className="space-y-2">
            <Field
              id="ch-current-installment"
              label={`¿Qué mensualidad te toca ahora de las ${term}?`}
              type="number"
              inputMode="numeric"
              min="1"
              max={String(term)}
              required
              value={currentInstallment}
              onChange={(event) => setCurrentInstallment(event.target.value)}
              helperText={`Si vas en el pago 3 de ${term}, escribe 3.`}
            />
            {!historicalFieldsValid && (
              <HelperText tone="danger" role="alert">
                {term < 2
                  ? 'Para una compra anterior a meses, escribe 2 o más mensualidades.'
                  : historicalAlreadyFinished
                    ? 'Esta compra ya está pagada por completo, así que no necesitas agregarla como una compra en curso.'
                    : `El número debe estar entre 1 y ${term}. Revisa cuál mensualidad te toca ahora.`}
              </HelperText>
            )}
          </div>
          <fieldset>
            <legend className="mb-2 text-sm font-semibold text-slate-800">
              ¿La mensualidad {installmentNumber} ya está pagada?
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex min-h-12 cursor-pointer items-center gap-2 rounded-xl border border-blue-100 bg-white px-3 py-2.5 text-sm text-slate-700">
                <input
                  type="radio"
                  name="current-installment-paid"
                  checked={!currentInstallmentPaid}
                  onChange={() => setCurrentInstallmentPaid(false)}
                  className="h-4 w-4"
                />
                No, todavía la debo
              </label>
              <label className="flex min-h-12 cursor-pointer items-center gap-2 rounded-xl border border-blue-100 bg-white px-3 py-2.5 text-sm text-slate-700">
                <input
                  type="radio"
                  name="current-installment-paid"
                  checked={currentInstallmentPaid}
                  onChange={() => setCurrentInstallmentPaid(true)}
                  className="h-4 w-4"
                />
                Sí, ya la pagué
              </label>
            </div>
          </fieldset>
          <fieldset className="lg:col-span-2">
            <legend className="mb-1 text-sm font-semibold text-slate-800">
              ¿Lo que falta de esta compra ya está incluido en los{' '}
              {formatMoney(card.balance, card.currency)} que debes?
            </legend>
            <p className="mb-2 text-xs leading-5 text-slate-600">
              Esto evita sumar la misma deuda dos veces. Si no estás seguro, revisa el saldo actual
              en la app de tu banco.
            </p>
            <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-2 lg:space-y-0">
              <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-blue-100 bg-white p-3">
                <input
                  type="radio"
                  name="historical-balance-treatment"
                  checked={balanceTreatment === 'alreadyIncluded'}
                  onChange={() => setBalanceTreatment('alreadyIncluded')}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-800">
                    Sí, ya está incluido
                  </span>
                  <span className="block text-xs leading-5 text-slate-500">
                    Guardaremos el plan, pero tu deuda total no cambiará.
                  </span>
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-blue-100 bg-white p-3">
                <input
                  type="radio"
                  name="historical-balance-treatment"
                  checked={balanceTreatment === 'addOutstanding'}
                  onChange={() => setBalanceTreatment('addOutstanding')}
                  className="mt-1 h-4 w-4"
                />
                <span>
                  <span className="block text-sm font-semibold text-slate-800">
                    No, todavía falta agregarlo
                  </span>
                  <span className="block text-xs leading-5 text-slate-500">
                    Sumaremos sólo lo que falta por pagar, no el precio completo.
                  </span>
                </span>
              </label>
            </div>
          </fieldset>
        </div>
      )}
      <label className="block">
        <span className="mb-1 block text-sm font-medium text-slate-700">Categoría (opcional)</span>
        <select
          className="tap-target w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-base text-slate-900"
          value={categoryId}
          onChange={(event) => setCategoryId(event.target.value)}
        >
          <option value="">Sin categoría (revisar después)</option>
          {expenseCategories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.icon ? `${category.icon} ` : ''}
              {category.name}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-xs leading-5 text-slate-500">
          La compra se mostrará en el mes en que la hiciste, no en el mes en que pagas la tarjeta.
        </span>
      </label>
      {amount > 0 && historicalFieldsValid && (
        <section
          className="rounded-2xl border border-slate-200 bg-slate-50 p-3.5"
          aria-live="polite"
        >
          <h3 className="text-sm font-bold text-slate-900">Revisa antes de guardar</h3>
          {isHistorical ? (
            <>
              <p className="mt-1 text-xs leading-5 text-slate-600">
                Precio original: {formatMoney(amount, card.currency)} · Aproximadamente{' '}
                {formatMoney(historical.monthlyAmount, card.currency)} al mes.
              </p>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                <div className="rounded-xl bg-white p-2.5">
                  <dt className="text-xs text-slate-500">Ya pagaste</dt>
                  <dd className="mt-0.5 font-semibold text-slate-800">
                    {historical.msiPaid} de {term}
                  </dd>
                </div>
                <div className="rounded-xl bg-white p-2.5">
                  <dt className="text-xs text-slate-500">Faltan</dt>
                  <dd className="mt-0.5 font-semibold text-slate-800">
                    {historical.monthsRemaining}{' '}
                    {historical.monthsRemaining === 1 ? 'pago' : 'pagos'}
                  </dd>
                </div>
                <div className="col-span-2 rounded-xl bg-white p-2.5 sm:col-span-1">
                  <dt className="text-xs text-slate-500">Falta por pagar</dt>
                  <dd className="mt-0.5 font-semibold text-slate-800">
                    {formatMoney(historical.remainingBalance, card.currency)}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-sm leading-5 text-slate-700">
                {historical.nextInstallment
                  ? `Tu siguiente pago es el ${historical.nextInstallment} de ${term}.`
                  : 'Esta compra ya está pagada por completo.'}
              </p>
            </>
          ) : (
            <p className="mt-1 text-sm leading-5 text-slate-600">
              {term === 1
                ? `Se agregarán ${formatMoney(amount, card.currency)} a tu deuda.`
                : `Se agregarán ${formatMoney(amount, card.currency)} a tu deuda y tendrás una mensualidad aproximada de ${formatMoney(monthlyCost, card.currency)}.`}
            </p>
          )}
          <div className="mt-3 border-t border-slate-200 pt-3 text-sm">
            <div className="flex justify-between gap-3 text-slate-600">
              <span>Deuda antes</span>
              <span>{formatMoney(card.balance, card.currency)}</span>
            </div>
            <div className="mt-1 flex justify-between gap-3 text-slate-600">
              <span>Cambio al guardar</span>
              <span>
                {balanceImpact > 0
                  ? `+${formatMoney(balanceImpact, card.currency)}`
                  : formatMoney(0, card.currency)}
              </span>
            </div>
            <div className="mt-2 flex justify-between gap-3 border-t border-slate-200 pt-2 font-bold text-slate-900">
              <span>Deuda después</span>
              <span>{formatMoney(resultingDebt, card.currency)}</span>
            </div>
          </div>
        </section>
      )}
      {amount > 0 && blockedByCredit && (
        <div className="rounded-xl border border-red-100 bg-red-50 p-3 text-sm lg:col-span-2">
          <p className="font-semibold text-expense">
            Este cambio supera el crédito disponible de tu tarjeta (
            {formatMoney(availableCredit, card.currency)}).
          </p>
          <p className="mt-1 text-xs leading-5 text-slate-600">
            Revisa la deuda y el límite en la app de tu banco. También puedes registrar primero un
            pago que ya hayas hecho.
          </p>
        </div>
      )}
      {policy && amount > 0 && !isHistorical && !blockedByCredit && (
        <div
          className={`rounded-xl border p-3 text-sm lg:col-span-2 ${requiresFlowException ? 'border-amber-200 bg-amber-50' : 'border-emerald-100 bg-emerald-50'}`}
        >
          {requiresFlowException ? (
            <>
              <p className="font-semibold text-amber-800">
                {exceedsPurchasePriceCap
                  ? `El precio completo supera el máximo recomendado de ${formatMoney(policy.purchasePriceCap)} para tu mes.`
                  : `La mensualidad de ${formatMoney(monthlyCost)} supera el monto recomendado de ${formatMoney(policy.newMonthlyMsiCapacity)}/mes.`}
              </p>
              {term > 1 && (
                <p className="mt-1 text-xs leading-5 text-amber-900">
                  Aunque pagues poco a poco, la app cuenta el precio completo en el mes de la compra
                  porque la deuda nace completa.
                </p>
              )}
              {exceedsPurchasePriceCap && exceedsMonthlyCap && (
                <p className="mt-1 text-xs leading-5 text-amber-900">
                  Además, la mensualidad supera el monto recomendado de{' '}
                  {formatMoney(policy.newMonthlyMsiCapacity)}/mes.
                </p>
              )}
              <label className="mt-2 flex min-h-11 cursor-pointer items-start gap-3 py-1 text-xs leading-5 text-amber-900">
                <input
                  type="checkbox"
                  checked={exceptionalPurchase}
                  onChange={(event) => setExceptionalPurchase(event.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 rounded border-amber-300 text-slate-900 focus:ring-blue-600"
                />
                <span>
                  Entiendo que esta compra reduce el dinero que tendré disponible este mes y aun así
                  quiero registrarla.
                </span>
              </label>
            </>
          ) : (
            <p className="text-emerald-800">
              {term === 1
                ? `El precio está dentro del máximo recomendado de ${formatMoney(policy.purchasePriceCap)}.`
                : `El precio completo y la mensualidad están dentro de lo recomendado para tus números.`}
            </p>
          )}
          {term > 1 && !requiresFlowException && (
            <p className="mt-1 text-xs leading-5 text-emerald-800">
              La app revisó tanto el precio completo como el pago mensual.
            </p>
          )}
        </div>
      )}
      {create.isError && (
        <p role="alert" className="text-sm text-expense lg:col-span-2">
          {getApiErrorMessage(create.error)}
        </p>
      )}
      <div className="modal-action-bar lg:col-span-2 lg:flex lg:justify-end">
        <button
          type="submit"
          disabled={
            create.isPending ||
            amount <= 0 ||
            !note.trim() ||
            !purchaseDateValid ||
            !historicalFieldsValid ||
            blockedByCredit ||
            (requiresFlowException && !exceptionalPurchase)
          }
          className="tap-target w-full rounded-lg bg-slate-900 px-4 py-2.5 text-base font-semibold text-white disabled:opacity-60 lg:w-auto lg:min-w-60"
        >
          {create.isPending
            ? 'Guardando…'
            : isHistorical
              ? 'Guardar compra anterior'
              : 'Guardar compra'}
        </button>
      </div>
    </form>
  );
}
